import React from "react";
import { Mail, Phone, Calendar, Linkedin } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button"; 
import { useLanguage } from "@/contexts/LanguageContext";

interface TeamMember {
  name: string;
  title: {
    fr: string;
    en: string;
    de: string;
  };
  image: string;
  email?: string;
  phone?: string;
  linkedin?: string;
}

const ContactRepresentative = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Besoin d'aide ?",
      titleHighlight: "Contactez-nous",
      subtitle: "", // Remove the previous subtitle text
      callToAction: "Réserver un appel",
      callToActionSubtitle: "Besoin d'aide pour préparer votre cadeau ? Réservez un appel avec notre équipe.",
      imageAlt: "membre de l'équipe Legatia"
    },
    en: {
      title: "Need help?",
      titleHighlight: "Contact us",
      subtitle: "Our team is available to answer all your questions and guide you through your journey.",
      callToAction: "Book a call",
      callToActionSubtitle: "Need help preparing your gift? Book a call with our team.",
      imageAlt: "Legatia team member"
    },
    de: {
      title: "Brauchen Sie Hilfe?",
      titleHighlight: "Kontaktieren Sie uns",
      subtitle: "Unser Team steht Ihnen zur Verfügung, um alle Ihre Fragen zu beantworten und Sie auf Ihrem Weg zu begleiten.",
      callToAction: "Einen Anruf buchen",
      callToActionSubtitle: "Brauchen Sie Hilfe bei der Vorbereitung Ihres Geschenks? Buchen Sie einen Anruf mit unserem Team.",
      imageAlt: "Legatia Teammitglied"
    }
  };

  const teamMembers: TeamMember[] = [
    {
      name: "Yanis",
      title: {
        fr: "Fondateur",
        en: "Founder",
        de: "Gründer"
      },
      image: "/lovable-uploads/034733cf-e0a0-42d2-8fa2-4d15b8f3150e.png",
      email: "yanis@legatia.fr",
      linkedin: "https://www.linkedin.com/in/yanis-taieb/"
    },
    {
      name: "Vanessa",
      title: {
        fr: "Responsable Relation Client",
        en: "Customer Relations Manager",
        de: "Kundenbeziehungsmanagerin"
      },
      image: "/lovable-uploads/e5561bb5-239b-4bdc-b02f-72dc8ee1b350.png",
      email: "cadeau@legatia.fr"
    }
  ];

  return (
    <section id="contact" className="section-padding bg-white border-t border-gray-100">
      <div className="container mx-auto max-w-7xl px-4">
        <div className="text-center mb-12">
          <h2 className="section-title">
            {language === 'fr' ? (
              <>Besoin d'aide ? <span className="gradient-text">Contactez-nous</span></>
            ) : language === 'en' ? (
              <>Need help? <span className="gradient-text">Contact us</span></>
            ) : (
              <>Brauchen Sie Hilfe? <span className="gradient-text">Kontaktieren Sie uns</span></>
            )}
          </h2>
          <p className="section-subtitle max-w-3xl mx-auto">
            {content[language].subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16 place-items-center">
          {teamMembers.map((member, index) => (
            <Card key={index} className="w-full max-w-sm border-legatia-yellow/20 shadow-lg overflow-hidden">
              <CardContent className="p-6">
                <div className="flex flex-col items-center gap-4">
                  <div className="rounded-full overflow-hidden border-4 border-legatia-light shadow-xl h-32 w-32">
                    <img 
                      src={member.image} 
                      alt={`${member.name}, ${content[language].imageAlt}`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold mb-1 text-legatia-dark">{member.name}</h3>
                    <p className="text-gray-600 mb-4 italic">{member.title[language]}</p>
                    
                    <div className="flex flex-col gap-2">
                      <a 
                        href={`mailto:${member.email}`} 
                        className="flex items-center justify-center gap-2 text-legatia-blue hover:text-legatia-orange transition-colors group"
                      >
                        <span className="bg-legatia-blue/10 p-1.5 rounded-full group-hover:bg-legatia-orange/10 transition-colors">
                          <Mail className="h-4 w-4" />
                        </span>
                        <span className="font-medium text-sm">{member.email}</span>
                      </a>
                      
                      {member.phone && (
                        <a 
                          href={`tel:${member.phone.replace(/\s/g, '')}`} 
                          className="flex items-center justify-center gap-2 text-legatia-blue hover:text-legatia-orange transition-colors group"
                        >
                          <span className="bg-legatia-blue/10 p-1.5 rounded-full group-hover:bg-legatia-orange/10 transition-colors">
                            <Phone className="h-4 w-4" />
                          </span>
                          <span className="font-medium text-sm">{member.phone}</span>
                        </a>
                      )}
                      
                      {member.linkedin && (
                        <a 
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 text-legatia-blue hover:text-legatia-orange transition-colors group"
                        >
                          <span className="bg-legatia-blue/10 p-1.5 rounded-full group-hover:bg-legatia-orange/10 transition-colors">
                            <Linkedin className="h-4 w-4" />
                          </span>
                          <span className="font-medium text-sm">LinkedIn</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="border-legatia-orange/30 bg-gradient-to-br from-white to-legatia-orange/5 shadow-lg overflow-hidden">
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center">
                <div className="bg-legatia-orange/20 p-3 rounded-full mb-4">
                  <Calendar className="h-8 w-8 text-legatia-orange" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{content[language].callToAction}</h3>
                <p className="text-gray-600 mb-6">{content[language].callToActionSubtitle}</p>
                <Button 
                  className="bg-legatia-orange hover:bg-legatia-orange/90 text-white px-6 py-2"
                  onClick={() => window.open('https://calendly.com/legatia/15min', '_blank')}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  {content[language].callToAction}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactRepresentative;
